import React, { useEffect, useState } from 'react';
import { RefreshCw, ArrowUpRight, ArrowDownRight, ChevronDown, ChevronUp, Clock } from 'lucide-react';
import { Panel } from '../../layout/Panel';
import { DataSourceBadge, DATA_SOURCES } from '../../ui/DataSourceBadge';
import { cbuApi } from '../../../services/api';
import type { ExchangeRateData } from '../../../services/api/cbuApi';

interface ExchangeRatesPanelProps {
  isMinimized?: boolean;
  onMinimize?: () => void;
}

// Top 4 currencies to show prominently
const TOP_CURRENCIES = ['USD', 'EUR', 'RUB', 'CNY'];

// Currency display names
const CURRENCY_NAMES: Record<string, string> = {
  USD: 'US Dollar',
  EUR: 'Euro',
  RUB: 'Russian Ruble',
  CNY: 'Chinese Yuan',
  GBP: 'British Pound',
  JPY: 'Japanese Yen',
  KZT: 'Kazakh Tenge',
  KRW: 'Korean Won',
  TRY: 'Turkish Lira',
};

export const ExchangeRatesPanel: React.FC<ExchangeRatesPanelProps> = ({
  isMinimized,
  onMinimize,
}) => {
  const [rates, setRates] = useState<ExchangeRateData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [source, setSource] = useState<'live' | 'cached' | 'static'>('static');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showAllCurrencies, setShowAllCurrencies] = useState(false);

  const fetchRates = async () => {
    try {
      setIsRefreshing(true);
      const response = await cbuApi.fetchExchangeRates();

      if (response.data) {
        setRates(response.data.rates);
        setLastUpdated(response.data.lastUpdated);
        setSource(response.data.source);
        setError(response.error);
      } else if (response.error) {
        setError(response.error);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch rates');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchRates();
    const interval = setInterval(fetchRates, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const formatRate = (rate: number, currency: string): string => {
    if (currency === 'JPY' || currency === 'KRW') {
      return rate.toFixed(2);
    }
    return rate.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatChange = (change: number): string => {
    const sign = change >= 0 ? '+' : '';
    return `${sign}${change.toFixed(2)}`;
  };

  // Split rates into top 4 and others
  const topRates = rates.filter(r => TOP_CURRENCIES.includes(r.currency));
  const otherRates = rates.filter(r => !TOP_CURRENCIES.includes(r.currency));

  // Export data
  const exportData = rates.map(r => ({
    currency: r.currency,
    rate: r.rate,
    change: r.change,
    changePercent: r.changePercent,
    date: r.date.toISOString(),
  }));

  return (
    <Panel
      id="exchange-rates"
      title="Exchange Rates"
      subtitle="UZS"
      status={source === 'live' ? 'live' : source === 'cached' ? 'delayed' : 'static'}
      isMinimized={isMinimized}
      onMinimize={onMinimize}
      data={exportData}
    >
      <div className="h-full flex flex-col p-3">
        {/* Last Updated - Prominent */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Clock size={12} className="text-gray-500" />
            <span className="text-xs text-gray-400">
              {lastUpdated
                ? lastUpdated.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
                  ' ' +
                  lastUpdated.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                : 'Loading...'}
            </span>
            {source === 'live' && (
              <span className="text-xs px-1.5 py-0.5 bg-data-green/20 text-data-green rounded">LIVE</span>
            )}
          </div>
          <button
            onClick={fetchRates}
            disabled={isRefreshing}
            className="p-1.5 hover:bg-terminal-border rounded transition-colors text-gray-500 hover:text-gray-300"
            title="Refresh rates"
          >
            <RefreshCw size={14} className={isRefreshing ? 'animate-spin' : ''} />
          </button>
        </div>

        {loading && !rates.length ? (
          <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">
            Loading exchange rates...
          </div>
        ) : (
          <>
            {/* Top 4 Currencies - 2x2 Grid */}
            <div className="grid grid-cols-2 gap-3 mb-3">
              {topRates.map(rate => (
                <div
                  key={rate.currency}
                  className="p-3 bg-terminal-border/40 rounded-lg border border-terminal-border hover:border-terminal-border/80 transition-colors"
                >
                  {/* Currency Header */}
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="text-base font-bold text-white">{rate.currency}</span>
                      <span className="text-gray-500 text-xs ml-1">/UZS</span>
                    </div>
                    {/* Change Indicator */}
                    <div className={`flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded ${
                      rate.change > 0
                        ? 'bg-data-green/20 text-data-green'
                        : rate.change < 0
                          ? 'bg-data-red/20 text-data-red'
                          : 'bg-gray-600/20 text-gray-500'
                    }`}>
                      {rate.change !== 0 && (
                        rate.change > 0 ? (
                          <ArrowUpRight size={12} />
                        ) : (
                          <ArrowDownRight size={12} />
                        )
                      )}
                      <span className="font-mono">{formatChange(rate.change)}</span>
                    </div>
                  </div>

                  {/* HERO Rate - Big Number */}
                  <div className="text-2xl md:text-3xl font-mono font-black text-white tracking-tight">
                    {formatRate(rate.rate, rate.currency)}
                  </div>

                  {/* Currency Name */}
                  <div className="text-xs text-gray-500 mt-1">
                    {CURRENCY_NAMES[rate.currency] || rate.currency}
                  </div>
                </div>
              ))}
            </div>

            {/* Expand/Collapse Other Currencies */}
            {otherRates.length > 0 && (
              <div className="border-t border-terminal-border/50 pt-2">
                <button
                  onClick={() => setShowAllCurrencies(!showAllCurrencies)}
                  className="w-full flex items-center justify-between py-2 px-2 text-xs text-gray-400 hover:text-gray-300 hover:bg-terminal-border/30 rounded transition-colors"
                >
                  <span>{showAllCurrencies ? 'Hide' : 'Show'} {otherRates.length} more currencies</span>
                  {showAllCurrencies ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>

                {/* Other Currencies List */}
                {showAllCurrencies && (
                  <div className="mt-2 space-y-1">
                    {otherRates.map(rate => (
                      <div
                        key={rate.currency}
                        className="flex items-center justify-between py-2 px-2 bg-terminal-border/20 rounded"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-mono font-semibold text-gray-300">{rate.currency}</span>
                          <span className="text-xs text-gray-600">{CURRENCY_NAMES[rate.currency]}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-mono text-white">
                            {formatRate(rate.rate, rate.currency)}
                          </span>
                          <div className={`flex items-center gap-0.5 text-xs ${
                            rate.change > 0 ? 'text-data-green' : rate.change < 0 ? 'text-data-red' : 'text-gray-500'
                          }`}>
                            {rate.change !== 0 && (
                              rate.change > 0 ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />
                            )}
                            <span className="font-mono">{formatChange(rate.change)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Footer */}
        <div className="mt-auto pt-2 border-t border-terminal-border/50">
          <DataSourceBadge
            source={{
              ...DATA_SOURCES.CBU,
              type: source === 'live' ? 'live' : source === 'cached' ? 'official' : 'verified',
              lastUpdated,
            }}
            showLabel
            size="xs"
          />
          {error && (
            <span className="text-xs text-amber-400/70 ml-2" title={error}>
              (fallback data)
            </span>
          )}
        </div>
      </div>
    </Panel>
  );
};

export default ExchangeRatesPanel;
